import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Switch,
  Chip,
  Autocomplete,
  IconButton,
  Paper,
  Divider,
  Stack,
  Collapse,
  Tooltip,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Layers as LayersIcon,
  ViewStream as MergedIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { tracksAPI, domainsAPI, riskCodesAPI, agentPromptsAPI, userAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import ConditionalBlockEditor from '../components/tracks/ConditionalBlockEditor'
import { getConfigComponent } from '../components/tracks/config'
import { useTrackTypes, getTypeDefaults } from '../hooks/useTrackTypes'
import PresentationFields, {
  toFormValues,
  mergePresentation,
  parsePresentationErrors,
} from '../components/presentation/PresentationFields'
import PathwayIcon from '../components/presentation/PathwayIcon'

// Helper to safely render values that might be objects
const renderValue = (value) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return value
}

function TrackBuilder() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { hasRole } = useAuth()
  const canPreviewMergedInstructions = hasRole('system_admin')
  const isNew = !id
  const { types: trackTypes, typesByCode, error: trackTypesError } = useTrackTypes()

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [domains, setDomains] = useState([])
  const [allTracks, setAllTracks] = useState([])
  const [riskCodes, setRiskCodes] = useState([])

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    track_type: 'education',
    domain: '',
    is_active: true,
    // Session duration
    min_session_duration_minutes: 5,
    max_session_duration_minutes: 15,
    // Requirements
    prerequisites: [],
    contraindications: [],
    // Agent Config
    metadata: {
      agent_instructions: '',
      conditional_blocks: [],
    },
    // Type-specific config
    type_config: {},
    // App presentation (display_name, description, icon) — see presentationForm
    presentation: {},
  })

  // App presentation edits are kept as raw strings and merged into
  // formData.presentation on save (blank = inherit the type default).
  const [presentationForm, setPresentationForm] = useState(toFormValues(null))
  const [presentationErrors, setPresentationErrors] = useState({})

  // Prompt Preview state
  const [previewExpanded, setPreviewExpanded] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] = useState(null)
  const [previewViewMode, setPreviewViewMode] = useState('merged') // 'merged' or 'layers'
  const [patients, setPatients] = useState([])
  const [previewContext, setPreviewContext] = useState({
    patient: null,
    time_of_day: 'morning',
    intervention_day: 14,
    engagement_score: 65,
    risk_factors: [],
  })

  useEffect(() => {
    fetchSupportingData()
    if (!isNew) {
      fetchTrack()
    }
  }, [id])

  const fetchSupportingData = async () => {
    try {
      const [domainsRes, tracksRes, riskCodesRes, patientsRes] = await Promise.all([
        domainsAPI.getAll(),
        tracksAPI.getAll(),
        riskCodesAPI.getAll().catch(() => ({ data: [] })),
        userAPI.getUsers().catch(() => ({ data: [] })),
      ])
      setDomains(domainsRes.data || [])
      setAllTracks(tracksRes.data || [])
      setRiskCodes(riskCodesRes.data || [])
      setPatients((patientsRes.data || []).filter((u) => u.user_type === 'patient'))
    } catch (err) {
      console.error('Error fetching supporting data:', err)
    }
  }

  const parseMetadata = (metadata) => {
    // Handle case where metadata is a JSON string
    let parsed = metadata
    if (typeof metadata === 'string') {
      try {
        parsed = JSON.parse(metadata)
      } catch {
        parsed = {}
      }
    }
    parsed = parsed || {}

    // Ensure agent_instructions is a string (handle case where it was saved as an object)
    if (typeof parsed.agent_instructions === 'object' && parsed.agent_instructions !== null) {
      parsed.agent_instructions = JSON.stringify(parsed.agent_instructions, null, 2)
    }

    return {
      agent_instructions: parsed.agent_instructions || '',
      conditional_blocks: Array.isArray(parsed.conditional_blocks) ? parsed.conditional_blocks : [],
      ...parsed,
    }
  }

  const fetchTrack = async () => {
    try {
      setLoading(true)
      const trackRes = await tracksAPI.getById(id)
      const track = trackRes.data
      setFormData({
        ...track,
        prerequisites: Array.isArray(track.prerequisites) ? track.prerequisites : [],
        contraindications: Array.isArray(track.contraindications) ? track.contraindications : [],
        metadata: parseMetadata(track.metadata),
        type_config: track.type_config || {},
        presentation: track.presentation || {},
      })
      setPresentationForm(toFormValues(track.presentation))
    } catch (err) {
      console.error('Error fetching pathway:', err)
      setError('Failed to load pathway. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (publish = false) => {
    // Education tracks are undeliverable without a module binding — the
    // conversation agent has nothing to teach (data-service enforces this
    // server-side too; validating here gives a friendlier error).
    if (formData.track_type === 'education' && !formData.type_config?.module_id) {
      setError('Education pathways require a module — select one in the Education configuration tab before saving.')
      return
    }
    // Same failure mode for activity pathways without an activity binding.
    if (formData.track_type === 'activity' && !formData.type_config?.activity_key) {
      setError('Activity pathways require an activity — select one in the Activity configuration before saving.')
      return
    }
    // On-demand pathways are unsearchable without at least one content group.
    if (
      ['information', 'resource_finder'].includes(formData.track_type) &&
      !(formData.type_config?.content_group_ids?.length > 0)
    ) {
      setError('This pathway requires at least one content group — select one in the Content Groups configuration before saving.')
      return
    }

    try {
      setSaving(true)
      setError(null)
      setPresentationErrors({})
      const dataToSave = {
        ...formData,
        is_active: publish ? true : formData.is_active,
        presentation: mergePresentation(formData.presentation, presentationForm),
      }

      if (isNew) {
        await tracksAPI.create(dataToSave)
      } else {
        await tracksAPI.update(id, dataToSave)
      }

      navigate('/tracks')
    } catch (err) {
      console.error('Error saving pathway:', err)
      const message = err.response?.data?.error || err.response?.data?.message
      // "Invalid presentation: presentation.display_name ..." → inline field errors
      const fieldErrors = parsePresentationErrors(message)
      setPresentationErrors(fieldErrors)
      setError(
        Object.keys(fieldErrors).length > 0
          ? 'Fix the highlighted App Presentation fields and save again.'
          : message || 'Failed to save pathway. Please try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const updateMetadata = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, [field]: value },
    }))
  }

  const addConditionalBlock = () => {
    updateMetadata('conditional_blocks', [
      ...(formData.metadata.conditional_blocks || []),
      {
        name: '',
        conditions: [{ field: '', op: 'eq', value: '' }],
        extend: { context_preamble: '' },
      },
    ])
  }

  const removeConditionalBlock = (index) => {
    const blocks = (formData.metadata.conditional_blocks || []).filter((_, i) => i !== index)
    updateMetadata('conditional_blocks', blocks)
  }

  // Fetch merged prompt preview
  const fetchPromptPreview = async () => {
    if (!formData.code && isNew) {
      setPreviewResult({ error: 'Please save the track first to preview merged instructions.' })
      return
    }

    setPreviewLoading(true)
    setPreviewResult(null)
    try {
      const contextOverrides = {
        time_of_day: previewContext.time_of_day,
        intervention_day: previewContext.intervention_day,
        engagement_score: previewContext.engagement_score,
        risk_factors: previewContext.risk_factors.map((rf) => ({
          code: rf.code || rf,
          severity: rf.severity || 'medium',
        })),
      }

      const response = await agentPromptsAPI.previewMerged({
        trackCode: formData.code,
        trackId: id ? parseInt(id) : undefined,
        userId: previewContext.patient?.id,
        contextOverrides,
      })

      setPreviewResult(response.data)
    } catch (err) {
      console.error('Error fetching prompt preview:', err)
      setPreviewResult({ error: err.response?.data?.error || 'Failed to load prompt preview' })
    } finally {
      setPreviewLoading(false)
    }
  }

  // Copy prompt to clipboard
  const copyPromptToClipboard = () => {
    if (previewResult?.merged_prompt) {
      navigator.clipboard.writeText(previewResult.merged_prompt)
    }
  }

  // Add risk factor to preview context
  const addPreviewRiskFactor = (riskCode) => {
    if (!riskCode) return
    setPreviewContext((prev) => ({
      ...prev,
      risk_factors: [...prev.risk_factors.filter((rf) => rf.code !== riskCode), { code: riskCode, severity: 'medium' }],
    }))
  }

  // Remove risk factor from preview context
  const removePreviewRiskFactor = (riskCode) => {
    setPreviewContext((prev) => ({
      ...prev,
      risk_factors: prev.risk_factors.filter((rf) => rf.code !== riskCode),
    }))
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  // Get tracks available for prerequisites (exclude self)
  const availableTracks = allTracks.filter((t) => t.id !== parseInt(id))

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/tracks')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5">
              {isNew ? 'Create Pathway' : `Edit: ${formData.name}`}
            </Typography>
            {!isNew && (
              <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                {formData.code}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Publish'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 0 }}>
        {/* ── Basic Info (no header, implicit) ── */}
        <Box sx={{ px: 3, py: 2 }}>
          <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center" maxWidth={700}>
            {/* Code */}
            <Typography variant="body2" color="text.secondary">Code</Typography>
            {isNew ? (
              <TextField
                size="small"
                value={formData.code}
                onChange={(e) => updateField('code', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                required
                placeholder="e.g., anxiety_psychoed_101"
                helperText="Unique identifier (cannot be changed)"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            ) : (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                {formData.code}
              </Typography>
            )}

            {/* Name */}
            <Typography variant="body2" color="text.secondary">Name</Typography>
            <TextField
              size="small"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
            />

            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'start', mt: 1 }}>Description</Typography>
            <TextField
              size="small"
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              multiline
              rows={2}
            />

            {/* Type */}
            <Typography variant="body2" color="text.secondary">Type</Typography>
            <FormControl size="small" sx={{ maxWidth: 220 }}>
              <Select
                value={formData.track_type}
                displayEmpty
                onChange={(e) => updateField('track_type', e.target.value)}
                error={Boolean(trackTypesError)}
              >
                {/* Keep the current value selectable while the registry loads */}
                {!typesByCode[formData.track_type] && (
                  <MenuItem value={formData.track_type}>{formData.track_type}</MenuItem>
                )}
                {trackTypes.map((type) => (
                  <MenuItem key={type.code} value={type.code}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {trackTypesError && (
                <Typography variant="caption" color="error">{trackTypesError}</Typography>
              )}
            </FormControl>

            {/* Domain */}
            <Typography variant="body2" color="text.secondary">Domain</Typography>
            <FormControl size="small" sx={{ maxWidth: 220 }}>
              <Select
                value={formData.domain}
                displayEmpty
                onChange={(e) => updateField('domain', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {domains.map((domain) => (
                  <MenuItem key={domain.code} value={domain.code}>
                    {domain.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Active */}
            <Typography variant="body2" color="text.secondary">Active</Typography>
            <Switch
              size="small"
              checked={formData.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
            />

            {/* Session Duration */}
            <Typography variant="body2" color="text.secondary">Duration (min)</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                size="small"
                type="number"
                value={formData.min_session_duration_minutes}
                onChange={(e) => updateField('min_session_duration_minutes', parseInt(e.target.value) || 5)}
                inputProps={{ min: 1, max: 60 }}
                sx={{ width: 80 }}
              />
              <Typography variant="body2" color="text.secondary">to</Typography>
              <TextField
                size="small"
                type="number"
                value={formData.max_session_duration_minutes}
                onChange={(e) => updateField('max_session_duration_minutes', parseInt(e.target.value) || 15)}
                inputProps={{ min: 1, max: 60 }}
                sx={{ width: 80 }}
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* ── App Presentation ── */}
        <Box sx={{ px: 3, py: 2 }}>
          {(() => {
            const typeDefaults = getTypeDefaults(typesByCode[formData.track_type])
            const placeholders = {
              display_name: formData.name || typeDefaults.label,
              description: 'No description shown',
              icon: typeDefaults.icon,
            }
            const previewName = presentationForm.display_name.trim() || placeholders.display_name
            const previewDescription = presentationForm.description.trim()
            const previewIcon = presentationForm.icon || placeholders.icon
            return (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 0.5 }}>
                  App Presentation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  How this pathway appears to patients in the app. Blank fields inherit the
                  pathway name and the {typeDefaults.label || formData.track_type} type default
                  (shown as placeholders); interventions can override these per attachment.
                </Typography>
                <PresentationFields
                  value={presentationForm}
                  onChange={setPresentationForm}
                  placeholders={placeholders}
                  errors={presentationErrors}
                  hints={{
                    display_name: 'Also the focus-step label',
                    description: 'Second line under the name',
                  }}
                />
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', maxWidth: 700 }}
                >
                  <PathwayIcon name={previewIcon} size={24} />
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {previewName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {previewDescription || `${typeDefaults.label || formData.track_type} · no description`}
                    </Typography>
                  </Box>
                </Box>
              </>
            )
          })()}
        </Box>

        <Divider />

        {/* ── Agent Config (type-specific config component provides its own header) ── */}
        <Box sx={{ px: 3, py: 2 }}>
          {/* Type-specific configuration */}
          {(() => {
            const TypeConfigComponent = getConfigComponent(formData.track_type)
            return TypeConfigComponent ? (
              <Box sx={{ mb: 3 }}>
                <TypeConfigComponent
                  config={formData.type_config || {}}
                  onChange={(newConfig) => updateField('type_config', newConfig)}
                  trackCode={formData.code}
                />
                <Divider sx={{ my: 3 }} />
              </Box>
            ) : null
          })()}

          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1 }}>
            Base Track Instructions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Merged with the base agent prompt at runtime.
            Use variables like {'{{patient_name}}'}, {'{{risk_factors}}'}.
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={6}
            value={formData.metadata?.agent_instructions || ''}
            onChange={(e) => updateMetadata('agent_instructions', e.target.value)}
            placeholder="Enter instructions for the AI agent when delivering this track..."
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
            sx={{ mb: 3 }}
          />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
                Conditional Instruction Blocks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Instructions that apply only when conditions are met.
              </Typography>
            </Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addConditionalBlock}>
              Add Block
            </Button>
          </Box>

          {(formData.metadata?.conditional_blocks || []).map((block, index) => (
            <ConditionalBlockEditor
              key={index}
              block={block}
              onChange={(updatedBlock) => {
                const blocks = [...(formData.metadata.conditional_blocks || [])]
                blocks[index] = updatedBlock
                updateMetadata('conditional_blocks', blocks)
              }}
              onRemove={() => removeConditionalBlock(index)}
            />
          ))}

          {(formData.metadata?.conditional_blocks || []).length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No conditional blocks defined. Add blocks to customize agent behavior based on patient context.
            </Alert>
          )}

          {/* Prompt Preview Section — system_admin only */}
          {canPreviewMergedInstructions && (
          <>
          <Divider sx={{ my: 3 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
                Preview Merged Instructions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See how base prompt, track instructions, and strategies merge together.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={previewExpanded ? null : <CodeIcon />}
              onClick={() => setPreviewExpanded(!previewExpanded)}
            >
              {previewExpanded ? 'Hide Preview' : 'Show Preview'}
            </Button>
          </Box>

          <Collapse in={previewExpanded}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              {/* Context Controls */}
              <Typography variant="subtitle2" gutterBottom>
                Preview Context
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    size="small"
                    options={patients}
                    getOptionLabel={(option) => option.name || ''}
                    value={previewContext.patient}
                    onChange={(e, newValue) =>
                      setPreviewContext((prev) => ({ ...prev, patient: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Patient (optional)" />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={previewContext.time_of_day}
                      displayEmpty
                      onChange={(e) =>
                        setPreviewContext((prev) => ({ ...prev, time_of_day: e.target.value }))
                      }
                    >
                      <MenuItem value="morning">Morning</MenuItem>
                      <MenuItem value="afternoon">Afternoon</MenuItem>
                      <MenuItem value="evening">Evening</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Intervention Day"
                    type="number"
                    value={previewContext.intervention_day}
                    onChange={(e) =>
                      setPreviewContext((prev) => ({
                        ...prev,
                        intervention_day: parseInt(e.target.value) || 1,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Engagement Score"
                    type="number"
                    value={previewContext.engagement_score}
                    onChange={(e) =>
                      setPreviewContext((prev) => ({
                        ...prev,
                        engagement_score: parseInt(e.target.value) || 50,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth size="small">
                    <Select
                      value=""
                      displayEmpty
                      onChange={(e) => addPreviewRiskFactor(e.target.value)}
                      renderValue={() => <Typography variant="body2" color="text.secondary">Add risk factor...</Typography>}
                    >
                      {riskCodes
                        .filter((rc) => !previewContext.risk_factors.find((rf) => rf.code === rc.code))
                        .map((rc) => (
                          <MenuItem key={rc.code} value={rc.code}>
                            {rc.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Selected Risk Factors */}
              {previewContext.risk_factors.length > 0 && (
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Risk Factors:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {previewContext.risk_factors.map((rf) => (
                      <Chip
                        key={rf.code}
                        label={rf.code}
                        size="small"
                        color="error"
                        variant="outlined"
                        onDelete={() => removePreviewRiskFactor(rf.code)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Generate Preview Button */}
              <Button
                variant="contained"
                size="small"
                startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={fetchPromptPreview}
                disabled={previewLoading || (isNew && !formData.code)}
                sx={{ mb: 2 }}
              >
                {previewLoading ? 'Generating...' : 'Generate Preview'}
              </Button>

              {/* Preview Result */}
              {previewResult && (
                <Box>
                  {previewResult.error ? (
                    <Alert severity="error">{previewResult.error}</Alert>
                  ) : (
                    <>
                      {/* View Mode Toggle */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary">
                          SOURCES:
                        </Typography>
                        <ToggleButtonGroup
                          value={previewViewMode}
                          exclusive
                          onChange={(e, value) => value && setPreviewViewMode(value)}
                          size="small"
                        >
                          <ToggleButton value="merged">
                            <Tooltip title="Merged View">
                              <MergedIcon fontSize="small" />
                            </Tooltip>
                          </ToggleButton>
                          <ToggleButton value="layers">
                            <Tooltip title="Layers View">
                              <LayersIcon fontSize="small" />
                            </Tooltip>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      {/* Source Attribution */}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        {previewResult.sources?.base_prompt && (
                          <Chip
                            label={`Base: ${previewResult.sources.base_prompt.agent_type}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          label={`Track: ${previewResult.sources?.track?.code}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                        {previewResult.sources?.track?.has_conditional_blocks && (
                          <Chip
                            label={`${previewResult.sources.track.conditional_block_count} conditional blocks`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {previewResult.sources?.applied_strategies?.map((s) => (
                          <Chip
                            key={s.id}
                            label={`Strategy: ${s.name}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ))}
                        {previewResult.sources?.strategy_tone && (
                          <Chip
                            label={`Tone: ${previewResult.sources.strategy_tone}`}
                            size="small"
                            color="info"
                          />
                        )}
                      </Stack>

                      {previewViewMode === 'merged' ? (
                        /* Merged Prompt Display */
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: 'grey.900',
                            color: 'grey.100',
                            maxHeight: 500,
                            overflow: 'auto',
                            position: 'relative',
                          }}
                        >
                          <Tooltip title="Copy to clipboard">
                            <IconButton
                              size="small"
                              onClick={copyPromptToClipboard}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'grey.400',
                                '&:hover': { color: 'grey.100' },
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              m: 0,
                            }}
                          >
                            {previewResult.merged_prompt}
                          </Typography>
                        </Paper>
                      ) : (
                        /* Layers View */
                        <Stack spacing={2}>
                          {/* Base Layer */}
                          {previewResult.layers?.base && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderLeft: 4,
                                borderColor: 'primary.main',
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip label="BASE PROMPT" size="small" color="primary" />
                                <Typography variant="caption" color="text.secondary">
                                  Agent type: {previewResult.sources?.base_prompt?.agent_type}
                                </Typography>
                              </Box>
                              <Stack spacing={1.5}>
                                {previewResult.layers.base.persona && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Persona
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                                      {renderValue(previewResult.layers.base.persona)}
                                    </Typography>
                                  </Box>
                                )}
                                {previewResult.layers.base.base_instructions && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Base Instructions
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      component="pre"
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        bgcolor: 'grey.50',
                                        p: 1,
                                        borderRadius: 1,
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        maxHeight: 200,
                                        overflow: 'auto',
                                      }}
                                    >
                                      {renderValue(previewResult.layers.base.base_instructions)}
                                    </Typography>
                                  </Box>
                                )}
                                <Box display="flex" gap={2}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Tone</Typography>
                                    <Chip label={previewResult.layers.base.default_tone || 'warm'} size="small" sx={{ ml: 1 }} />
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Approach</Typography>
                                    <Chip label={previewResult.layers.base.default_approach || 'supportive'} size="small" sx={{ ml: 1 }} />
                                  </Box>
                                </Box>
                              </Stack>
                            </Paper>
                          )}

                          {/* Track Layer */}
                          {previewResult.layers?.track && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderLeft: 4,
                                borderColor: 'secondary.main',
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip label="TRACK LAYER" size="small" color="secondary" />
                                <Typography variant="caption" color="text.secondary">
                                  {previewResult.sources?.track?.code}
                                </Typography>
                              </Box>
                              <Stack spacing={1.5}>
                                {previewResult.layers.track.context_preamble && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Context Preamble
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'secondary.50', p: 1, borderRadius: 1 }}>
                                      {renderValue(previewResult.layers.track.context_preamble)}
                                    </Typography>
                                  </Box>
                                )}
                                {previewResult.layers.track.approach && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Approach Override
                                    </Typography>
                                    <Chip label={renderValue(previewResult.layers.track.approach)} size="small" color="secondary" variant="outlined" />
                                  </Box>
                                )}
                                {previewResult.layers.track.tone && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Tone Override
                                    </Typography>
                                    <Chip label={renderValue(previewResult.layers.track.tone)} size="small" color="secondary" variant="outlined" />
                                  </Box>
                                )}
                                {previewResult.layers.track.response_guidance && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Response Guidance
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'secondary.50', p: 1, borderRadius: 1 }}>
                                      {renderValue(previewResult.layers.track.response_guidance)}
                                    </Typography>
                                  </Box>
                                )}
                                {previewResult.layers.track.conditional_blocks_active?.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Active Conditional Blocks ({previewResult.layers.track.conditional_blocks_active.length} of {previewResult.layers.track.conditional_blocks_total})
                                    </Typography>
                                    <Stack spacing={1} mt={0.5}>
                                      {previewResult.layers.track.conditional_blocks_active.map((block, idx) => (
                                        <Paper key={idx} sx={{ p: 1, bgcolor: 'info.50' }}>
                                          <Typography variant="body2" fontWeight="medium">{block.name}</Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Conditions: {JSON.stringify(block.conditions)}
                                          </Typography>
                                          {block.extend && Object.keys(block.extend).length > 0 && (
                                            <Box mt={0.5}>
                                              {Object.entries(block.extend).map(([key, value]) => (
                                                <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                  +{key}: {typeof value === 'string' ? value : JSON.stringify(value)}
                                                </Typography>
                                              ))}
                                            </Box>
                                          )}
                                        </Paper>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                                {!previewResult.layers.track.context_preamble &&
                                 !previewResult.layers.track.approach &&
                                 !previewResult.layers.track.tone &&
                                 !previewResult.layers.track.response_guidance &&
                                 !previewResult.layers.track.conditional_blocks_active?.length && (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    No track-specific modifications configured
                                  </Typography>
                                )}
                              </Stack>
                            </Paper>
                          )}

                          {/* Strategy Layer */}
                          {(previewResult.layers?.strategies?.applied_count > 0 ||
                            previewResult.layers?.strategies?.tone_override) && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderLeft: 4,
                                borderColor: 'warning.main',
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip label="STRATEGY LAYER" size="small" color="warning" />
                                <Typography variant="caption" color="text.secondary">
                                  {previewResult.layers?.strategies?.applied_count} strategies applied
                                </Typography>
                              </Box>
                              <Stack spacing={1.5}>
                                {previewResult.layers?.strategies?.tone_override && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Tone Override
                                    </Typography>
                                    <Chip
                                      label={previewResult.layers.strategies.tone_override}
                                      size="small"
                                      color="warning"
                                    />
                                  </Box>
                                )}
                                {previewResult.layers?.strategies?.applied_names?.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Applied Strategies
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                      {previewResult.layers.strategies.applied_names.map((name) => (
                                        <Chip key={name} label={name} size="small" color="warning" variant="outlined" />
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                                {previewResult.layers?.strategies?.prompt_modifications?.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Prompt Modifications
                                    </Typography>
                                    <Stack spacing={1} mt={0.5}>
                                      {previewResult.layers.strategies.prompt_modifications.map((mod, idx) => (
                                        <Paper key={idx} sx={{ p: 1, bgcolor: 'warning.50' }}>
                                          <Typography variant="body2" fontWeight="medium">{mod.strategy_name}</Typography>
                                          {Object.entries(mod).filter(([k]) => k !== 'strategy_name').map(([key, value]) => (
                                            <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                              +{key}: {typeof value === 'string' ? value : JSON.stringify(value)}
                                            </Typography>
                                          ))}
                                        </Paper>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                              </Stack>
                            </Paper>
                          )}

                          {/* No strategy modifications notice */}
                          {!previewResult.layers?.strategies?.applied_count &&
                           !previewResult.layers?.strategies?.tone_override && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderLeft: 4,
                                borderColor: 'grey.300',
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip label="STRATEGY LAYER" size="small" />
                              </Box>
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                No strategies matched the current context
                              </Typography>
                            </Paper>
                          )}
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Paper>
          </Collapse>
          </>
          )}
        </Box>

        <Divider />

        {/* ── Requirements ── */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1.5 }}>
            Requirements
          </Typography>

          <Box maxWidth={700}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Prerequisites</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mb: 1, display: 'block' }}>
              Patient must have completed these pathways before this one is eligible.
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={availableTracks}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              value={availableTracks.filter((t) => formData.prerequisites.includes(t.code))}
              onChange={(e, newValue) =>
                updateField('prerequisites', newValue.map((t) => t.code))
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select prerequisite pathways" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.code}
                  />
                ))
              }
              sx={{ mb: 3 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Contraindications</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mb: 1, display: 'block' }}>
              This track will be excluded when these risk factors are present.
            </Typography>
            {riskCodes.length > 0 ? (
              <Autocomplete
                multiple
                size="small"
                options={riskCodes}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                value={riskCodes.filter((r) => formData.contraindications.includes(r.code))}
                onChange={(e, newValue) =>
                  updateField('contraindications', newValue.map((r) => r.code))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select risk codes that exclude this track" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      size="small"
                      color="error"
                      variant="outlined"
                      {...getTagProps({ index })}
                      key={option.code}
                    />
                  ))
                }
              />
            ) : (
              <Alert severity="info">
                No risk codes configured yet. Create risk codes in the Risk Configuration section.
              </Alert>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default TrackBuilder
